import { useState, useMemo } from 'react';
import { 
  Activity, 
  Store, 
  BookOpen, 
  Bot, 
  ShieldCheck, 
  Search, 
  Calendar, 
  Clock, 
  CheckCircle2, 
  Layers, 
  ArrowUpRight,
  Filter,
  RefreshCw
} from 'lucide-react';
import { Badge } from '../../../components/ui/Badge.jsx';
import { Input } from '../../../components/ui/Input.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { cn } from '../../../lib/utils.js';
import { INITIAL_CHANGELOG_EVENTS } from '../executive-data.js';

export const CHANGELOG_FILTERS = [
  { id: 'all', label: 'Alle Ereignisse', category: null, icon: Activity },
  { id: 'shop', label: 'Shop & Katalog', category: 'Shop', icon: Store },
  { id: 'rag', label: 'RAG & Dokumente', category: 'RAG', icon: BookOpen },
  { id: 'alwis', label: 'ALWIS & Planung', category: 'ALWIS', icon: Bot },
  { id: 'security', label: 'Sicherheit & Audit', category: 'Security', icon: ShieldCheck },
];

export const CATEGORY_COLOR_MAP = {
  Shop: {
    badge: 'bg-blue-50 text-blue-700 border-blue-200',
    icon: 'text-blue-600 bg-blue-50 border-blue-100',
  },
  RAG: {
    badge: 'bg-purple-50 text-purple-700 border-purple-200',
    icon: 'text-purple-600 bg-purple-50 border-purple-100',
  },
  ALWIS: {
    badge: 'bg-red-50 text-[var(--primary,#e3000b)] border-red-200',
    icon: 'text-[var(--primary,#e3000b)] bg-red-50 border-red-100',
  },
  Security: {
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    icon: 'text-emerald-700 bg-emerald-50 border-emerald-100',
  },
};

export function ChangelogFeed({
  events = INITIAL_CHANGELOG_EVENTS,
  onAddEvent,
  activeFilter = 'all',
  className,
}) {
  const [selectedFilter, setSelectedFilter] = useState(activeFilter);
  const [searchQuery, setSearchQuery] = useState('');

  // Combine and sort events chronologically descending
  const sortedEvents = useMemo(() => {
    const list = Array.isArray(events) ? [...events] : [];
    return list.sort((a, b) => new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime());
  }, [events]);

  // Compute counts per category
  const counts = useMemo(() => {
    const map = { all: sortedEvents.length, shop: 0, rag: 0, alwis: 0, security: 0 };
    sortedEvents.forEach((evt) => {
      const cat = (evt.category || '').toLowerCase();
      if (cat === 'shop') map.shop++;
      else if (cat === 'rag') map.rag++;
      else if (cat === 'alwis') map.alwis++;
      else if (cat === 'security') map.security++;
    });
    return map;
  }, [sortedEvents]);

  // Filtered events
  const filteredEvents = useMemo(() => {
    return sortedEvents.filter((event) => {
      // Category filter
      if (selectedFilter !== 'all') {
        const filterDef = CHANGELOG_FILTERS.find((f) => f.id === selectedFilter);
        if (filterDef?.category && event.category !== filterDef.category) {
          return false;
        }
      }

      // Text search
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const inTitle = (event.title || '').toLowerCase().includes(q);
        const inDesc = (event.description || '').toLowerCase().includes(q);
        const inBadge = (event.badge || '').toLowerCase().includes(q);
        return inTitle || inDesc || inBadge;
      }

      return true;
    });
  }, [sortedEvents, selectedFilter, searchQuery]);

  return (
    <div
      data-testid="changelog-feed"
      className={cn(
        'space-y-6 rounded-2xl border border-[var(--border,#e2e8f0)] bg-white p-6 sm:p-8 shadow-xs',
        className
      )}
    >
      {/* Feed Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 pb-6 border-b border-[var(--border,#e2e8f0)]">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-50 text-[var(--primary,#e3000b)]">
              <Activity className="h-4 w-4" />
            </div>
            <h2 className="font-display text-lg sm:text-xl font-bold text-[var(--foreground,#181716)]">
              Transparenter Plattform Update- &amp; Changelog-Feed
            </h2>
          </div>
          <p className="text-xs sm:text-sm text-[var(--secondary,#475569)]">
            Chronologischer Audit- und Synchronisationsverlauf der ASL Ademco B2B Plattform, RAG-Ingestion und ALWIS Planungs-Engine.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Badge
            variant="outline"
            data-testid="changelog-event-count"
            className="bg-slate-100 text-slate-800 border-slate-300 text-xs font-mono font-bold py-1 px-3"
          >
            {`${filteredEvents.length} von ${sortedEvents.length} Einträgen`}
          </Badge>
        </div>
      </div>

      {/* Filter Tabs & Search Bar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        {/* Category Filter Pills */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1" aria-label="Changelog Filter">
          {CHANGELOG_FILTERS.map((f) => {
            const Icon = f.icon;
            const isSelected = selectedFilter === f.id;
            const count = counts[f.id] || 0;

            return (
              <button
                key={f.id}
                type="button"
                data-testid={`changelog-filter-${f.id}`}
                onClick={() => setSelectedFilter(f.id)}
                className={cn(
                  'flex items-center gap-2 rounded-xl px-3.5 py-2 text-xs font-bold transition-all cursor-pointer select-none shrink-0',
                  isSelected
                    ? 'bg-[var(--primary,#e3000b)] text-white shadow-xs'
                    : 'bg-[var(--surface-container-low,#f8fafc)] text-slate-700 border border-slate-200 hover:bg-slate-100 hover:text-slate-900'
                )}
              >
                <Icon className={cn('h-3.5 w-3.5', isSelected ? 'text-white' : 'text-[var(--primary,#e3000b)]')} />
                <span>{f.label}</span>
                <span
                  className={cn(
                    'rounded-full px-1.5 py-0.2 text-[10px] font-mono font-bold',
                    isSelected ? 'bg-white/25 text-white' : 'bg-slate-200 text-slate-700'
                  )}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Search Input */}
        <div className="relative w-full md:w-64">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
          <Input
            data-testid="changelog-search-input"
            type="text"
            placeholder="Ereignisse filtern..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-9 pl-8 text-xs bg-[var(--surface-container-low,#f8fafc)] border-slate-200"
          />
        </div>
      </div>

      {/* Events Timeline List */}
      <div 
        data-testid="changelog-event-list"
        className="space-y-3"
      >
        {filteredEvents.length === 0 ? (
          <div className="rounded-xl border border-slate-200 bg-[var(--surface-container-low,#f8fafc)] p-8 text-center text-xs text-slate-500">
            Keine Ereignisse für den aktuellen Filter oder Suchbegriff gefunden.
          </div>
        ) : (
          filteredEvents.map((event) => {
            const colorStyle = CATEGORY_COLOR_MAP[event.category] || CATEGORY_COLOR_MAP.Shop;
            const Icon = event.category === 'Shop' ? Store :
                         event.category === 'RAG' ? BookOpen :
                         event.category === 'ALWIS' ? Bot : ShieldCheck;

            return (
              <div
                key={event.id}
                data-testid="changelog-event-item"
                className="group flex flex-col sm:flex-row sm:items-start justify-between gap-4 rounded-xl border border-slate-200 bg-white p-4 transition-all hover:border-slate-300 hover:shadow-xs"
              >
                <div className="flex items-start gap-3 min-w-0">
                  <div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border', colorStyle.icon)}>
                    <Icon className="h-4 w-4" />
                  </div>

                  <div className="space-y-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge
                        variant="outline"
                        className={cn('text-[10px] font-bold px-2 py-0.5 uppercase tracking-wider', colorStyle.badge)}
                      >
                        {event.badge || event.category}
                      </Badge>
                      <h4 className="font-display text-xs sm:text-sm font-bold text-slate-900 truncate">
                        {event.title}
                      </h4>
                    </div>

                    <p className="text-xs text-slate-600 leading-relaxed">
                      {event.description}
                    </p>

                    {event.meta && (
                      <div className="pt-1 text-[11px] font-mono text-slate-400">
                        {typeof event.meta === 'string' ? event.meta : JSON.stringify(event.meta)}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex sm:flex-col items-center sm:items-end justify-between sm:justify-start gap-1 shrink-0 pt-0.5">
                  <span className="font-mono text-[11px] text-slate-500 font-medium whitespace-nowrap">
                    {event.formattedDate || event.timestamp}
                  </span>
                  {event.isVerified === true || event.status === 'verified' ? (
                    <div className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700" data-testid="changelog-verified-badge">
                      <CheckCircle2 className="h-3 w-3" />
                      <span>Verifiziert</span>
                    </div>
                  ) : (
                    <div className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200" data-testid="changelog-demo-badge">
                      <span>Demo-Ereignis</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
